function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

const _ = require('lodash');

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const {
  poll
} = require('../utils/poll');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const diff = require('../utils/diff');

const Address = require('../entities/address');

const Location = require('../entities/location');

const PagedList = require('../entities/paged-list');

const OffersRepo = require('./offers-repo');

const StatsRepo = require('./stats-repo');

const ImageLinksRepo = require('./image-links-repo');

const UsersRepo = require('./users-repo');

const {
  strinct,
  strList,
  nullable
} = require('../utils/serialize');

const subtractPagedLists = require('../utils/subtract-paged-lists');

const leftOuterJoinPagedLists = require('../utils/left-outer-join-paged-lists');

const TagsRepo = require('./tags-repo');

const getPropertyLocales = require('../utils/get-property-locales');

const LocationLocalizerFactory = require('../entities/location-localizer');

const LocationLocalizationDictionary = require('../entities/location-localization-dictionary');

const TYPE_NAME = TagsRepo.TYPE_NAMES.location;

const identity = i => i;

const externalPlaceIdFields = ['osm', 'google', 'yelp', 'directwest'];
const selfSearchFields = ['id', 'name', 'slug', 'url', 'phone', 'address(id,streetAddress,street,city,state,country,centre,suite,postalcode)', 'business(id,name,logoStandardImages(url))', 'tags(name)'];
const searchFields = [...selfSearchFields, `externalPlaceIds(${externalPlaceIdFields.join(',')})`, 'socialProfiles'];
const selfPropertyLocationsSearchFields = ['id', 'name', 'slug', 'url', 'phone', 'address(id,streetAddress,street,city,state,country,centre,suite,postalcode)', 'business(id,name,tags(name))', 'policies(plan(id,name))', 'tags(name)'];
const propertyLocationsSearchFields = [...selfPropertyLocationsSearchFields, `externalPlaceIds(${externalPlaceIdFields.join(',')})`];
const fields = ['id', 'name', 'slug', 'url', 'phone', 'socialProfiles'];

const parse = json => new Location({
  id: json.id,
  name: json.name,
  // All Location addresses are implicitly complete
  address: new Address(Object.assign({
    isComplete: true
  }, json.address)),
  slug: json.slug,
  url: json.url,
  phone: json.phone,
  enabled: json.isEnabled !== false,
  business: json.business,
  policies: json.policies,
  placeIds: json.externalPlaceIds,
  tags: json.tags,
  socialProfiles: json.socialProfiles
});

const serializeUpdate = location => {
  const socialProfiles = location.socialProfiles ? location.socialProfiles.filter(p => !!p.id) : location.socialProfiles;
  return {
    id: location.id,
    name: strinct(location.name),
    url: nullable(strinct)(location.url),
    phone: strinct(location.phone),
    addressId: _.get(location, 'address.id'),
    socialProfiles
  };
};

const serializeCreate = location => ({
  id: location.id,
  name: strinct(location.name),
  url: strinct(location.url),
  phone: strinct(location.phone),
  addressId: _.get(location, 'address.id')
});

const getUpdatedAddress = _async(function (address
/*: Address*/
, client
/*: ApiClient*/
) {
  return address ? client.addresses.create(address) : address;
});

const listLocationsForBusiness = (client, businessId, params) => {
  const path = `/businesses/${businessId}/locations/search`;
  const req = new client.Request(path, params, searchFields);
  req.parse = parse;
  return client.get(req);
};

const listLocationsForPropertyAndBusiness = (client, propertyId, businessId, params) => {
  const path = `/properties/${propertyId}/businesses/${businessId}/locations/search`;
  const req = new client.Request(path, params, searchFields);
  req.parse = parse;
  return client.get(req).then(pList => pList.copy(items => items.map(item => {
    // eslint-disable-next-line no-param-reassign
    item.enabled = params.enabled;
    return item;
  })));
};

class LocationRepo {
  constructor(client, id, parentContext) {
    this.id = id;
    this.context = parentContext.clone({
      location: id
    });
    this.client = client;
    this.offers = new OffersRepo(client, this.context);
    this.stats = new StatsRepo(client, this.context);
    this.images = new ImageLinksRepo(client, this.context);
    this.users = new UsersRepo(client, this.context);
  }

  adminDetails()
  /*: Promise<Location>*/
  {
    const {
      businessId,
      locationId
    } = this.context && this.context.serialize() || {}; // We always get Location without propertyId context because we want to see it even if it's not enabled

    const path = `/businesses/${businessId}/locations/search`;
    const req = new this.client.Request(path, {
      locationId
    }, searchFields);
    req.parse = parse;
    return this.client.get(req).then(list => list.items[0]);
  }

  details()
  /*: Promise<Location>*/
  {
    // Just for Business and Location level Roles
    const {
      propertyId,
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/properties/${propertyId}/businesses/${businessId}/locations/search`;
    const req = new this.client.Request(path, {
      locationId
    }, searchFields);
    req.parse = parse;
    return this.client.get(req).then(list => list.items[0]).then(this.client.enrichment.readTap(this.context.serialize(), (location, updates) => Object.assign(location, updates)));
  }

  enable()
  /*: Promise<Object>*/
  {
    const {
      propertyId,
      businessId
    } = this.context.serialize();
    return this.client.policy.addLocation(propertyId, businessId, this.id).then(this.client.enrichment.writeTap(this.context.serialize(), () => ({
      isEnabled: true
    })));
  }

  disable()
  /*: Promise<Object>*/
  {
    const {
      propertyId,
      businessId
    } = this.context.serialize();
    return this.client.policy.removeLocation(propertyId, businessId, this.id).then(this.client.enrichment.writeTap(this.context.serialize(), () => ({
      isEnabled: false
    })));
  }

  editWithLocalization()
  /*: Promise<Location>*/
  {
    const _this = this;

    return _call(function () {
      return _await(Promise.all([_this.adminDetails(), _this.getLocalizations().then(r => r.items), getPropertyLocales(_this)]), function ([location, localizations, locales]) {
        return LocationLocalizerFactory(locales).setLocalizations(location, localizations);
      });
    });
  }

  update(original
  /*: Location | Object*/
  = {}, updates
  /*: Location*/
  )
  /*: Promise<Location>*/
  {
    const _this2 = this;

    return _call(function () {
      const {
        locationId,
        businessId
      } = _this2.context.serialize();

      return _await(getPropertyLocales(_this2), function (locales) {
        const locationLocalizer = LocationLocalizerFactory(locales);
        const locationUpdates = locationLocalizer.applyDefaultLocalizations(updates);
        const updatedFields = diff(original, locationUpdates);
        const path = `/businesses/${businessId}/locations/${locationId}`; // Update Address

        return _await(getUpdatedAddress(updatedFields.address, _this2.client), function (updatedAddress) {
          updatedFields.address = updatedAddress; // Update Location

          const req = new _this2.client.Request(path, updatedFields, fields);
          req.parse = parse;
          req.serialize = serializeUpdate;
          return _await(_this2.client.patch(req), function (updatedLocation) {
            // Fake "updated" address
            updatedLocation.address = updatedAddress || original.address; // eslint-disable-line no-param-reassign
            // Update Localizations

            return _await(_this2.updateLocalizations(locationLocalizer.getLocalizations(updates)), function () {
              return locationUpdates.slug && locationUpdates.slug === original.slug ? updatedLocation : _await(_this2.updateSlug(locationUpdates.slug || locationUpdates.name), function (updatedSlug) {
                // Fake "updated" slug
                updatedLocation.slug = updatedSlug; // eslint-disable-line no-param-reassign

                return updatedLocation;
              });
            }); // Update Slug
          });
        });
      });
    });
  }

  updateLocalizations(localizations) {
    const _this3 = this;

    return _call(function () {
      return Promise.all(localizations.map(localization => _this3.setLocalization(localization)));
    });
  }

  updateSlug(newSlug
  /*: string*/
  )
  /*: Promise<string>*/
  {
    const {
      locationId,
      businessId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/${locationId}/slugs`;
    const req = new this.client.Request(path, {
      inputString: newSlug
    }, ['slug']);
    return this.client.post(req).then(({
      slug
    }) => slug);
  }

  getTags()
  /*: (Promise<any>)*/
  {
    const {
      propertyId,
      locationId
    } = this.context.serialize();
    return this.client.tags.list(propertyId, TYPE_NAME, locationId);
  }

  updateTags(newTags
  /*: Array<string>*/
  )
  /*: (Promise<any>)*/
  {
    const {
      propertyId,
      locationId
    } = this.context.serialize();
    return this.client.tags.reconcile(propertyId, TYPE_NAME, locationId, newTags);
  }

  addTag(tagName
  /*: string*/
  )
  /*: (Promise<any>)*/
  {
    const {
      propertyId,
      locationId
    } = this.context.serialize();
    return this.client.tags.create(propertyId, TYPE_NAME, locationId, tagName).then(() => true).catch(() => false);
  }

  removeTag(tagName
  /*: string*/
  )
  /*: (Promise<any>)*/
  {
    const {
      propertyId,
      locationId
    } = this.context.serialize();
    return this.client.tags.delete(propertyId, TYPE_NAME, locationId, tagName).then(() => true).catch(() => false);
  }

  delete()
  /*: Promise<boolean>*/
  {
    const {
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/${locationId}`;
    const req = new this.client.Request(path);
    return this.client.delete(req);
  }

  autoLink()
  /*: Promise<Boolean>*/
  {
    // Automatically link to existing policy
    const {
      propertyId,
      businessId,
      locationId
    } = this.context.serialize();
    if (!propertyId) throw new Error('Missing required propertyId');
    if (!businessId) throw new Error('Missing required businessId');
    if (!locationId) throw new Error('Missing required locationId');
    return this.client.policy.addLocation(propertyId, businessId, locationId);
  }

  waitForIndex(compareFn
  /*: Function*/
  , onTimeout
  /*: Function*/
  )
  /*: Promise<boolean>*/
  {
    const _this4 = this;

    return _call(function () {
      const {
        propertyId,
        businessId,
        locationId
      } = _this4.context.serialize();

      const path = [propertyId && `/properties/${propertyId}`, businessId && `/businesses/${businessId}`, `/locations/search`].filter(Boolean).join('');
      const params = {
        locationId
      };
      const req = new _this4.client.Request(path, params, searchFields);

      const predicate = _async(function ()
      /*: Promise<boolean>*/
      {
        return _await(_this4.client.get(req), function (result) {
          return result.items.length ? compareFn ? compareFn(result.items[0]) : true : false;
        });
      });

      return _await(poll(predicate, 1000, 5000).catch(err => {
        if (!err.isTimeout) throw err;
        /* eslint-disable-next-line no-console */

        console.error(new Error('Waiting for location for more than 5 seconds...'));
        return poll(predicate, 1000, 55000, onTimeout);
      }));
    });
  }

  getLocalizations()
  /*: Promise<PagedList<any>>*/
  {
    const ALL_LOCALE_FIELDS = ['id', 'type', 'modelId', 'locale', 'content', 'priority'];
    const {
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/${locationId}/localizations`;
    const req = new this.client.Request(path, {}, ALL_LOCALE_FIELDS);
    req.parse = LocationLocalizationDictionary.fromJson;
    return this.client.get(req);
  }

  setLocalization({
    locale,
    content
  })
  /*: Promise<any>*/
  {
    const setFields = ['id'];
    const {
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/${locationId}/localizations`;
    const params = {
      locale,
      content
    };
    const req = new this.client.Request(path, params, setFields);
    req.serialize = LocationLocalizationDictionary.serialize;
    return this.client.post(req);
  }

  getValidations()
  /*: Promise<any>*/
  {
    const getFields = [];
    const {
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/${locationId}/validation`;
    const req = new this.client.Request(path, {}, getFields);
    return this.client.get(req);
  }

  getExternalPlaceIds() {
    const {
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/search`;
    const req = new this.client.Request(path, {
      locationId
    }, [`externalPlaceIds(${externalPlaceIdFields.join(',')})`]);
    return this.client.get(req).then(list => _.get(list, ['items', 0, 'externalPlaceIds']));
  }

  setExternalPlaceIds(payload
  /*: Object*/
  ) {
    /* eslint-disable function-paren-newline */
    const strippedPayload = Object.fromEntries(Object.entries(payload).filter(entry => !!entry[1]));
    /* eslint-enable function-paren-newline */

    const {
      businessId,
      locationId
    } = this.context.serialize();
    const path = `/businesses/${businessId}/locations/${locationId}/externalPlaceIds`;
    const req = new this.client.Request(path, strippedPayload, externalPlaceIdFields);
    return this.client.post(req);
  }

}

class LocationsRepo {
  constructor(client
  /*: ApiClient*/
  , parentContext
  /*: Object*/
  ) {
    this.client = client;
    this.context = parentContext;

    this.for = (id
    /*: locationId*/
    ) => new LocationRepo(client, id, this.context);
  }
  /** FIXME: Split list* methods into list* and search*
   *   search:
   *    propertyId: implies handling an 'enabled' flag
   *    no property id:
   */

  /**
   * List Locations
   * @param query
   * @param onlyEnabled: Only Locations added to Business+Property AND enabled
   * @param onlyExcluded: Only Locations not added to Business+Property
   * @returns Promise<PagedList<Location>>
   */
  // FIXME: Paging still won't work for a) "all Locations", b) "onlyExcluded"


  listAdmin({
    /*: {
        query: string,
        tags: ?Array<string>,
        onlyEnabled: boolean,
        onlyExcluded: boolean
      }*/

    query,
    tags,
    onlyEnabled = false,
    onlyExcluded = false
  } = {})
  /*: (Promise<PagedList<Location>>)*/
  {
    const {
      propertyId,
      businessId
    } = this.context && this.context.serialize();

    if (!propertyId) {
      return listLocationsForBusiness(this.client, businessId, {
        query,
        tagNames: strList(tags)
      });
    }

    if (onlyEnabled) {
      return listLocationsForPropertyAndBusiness(this.client, propertyId, businessId, {
        query,
        tagNames: strList(tags),
        enabled: true
      });
    }

    if (onlyExcluded) {
      return Promise.all([listLocationsForBusiness(this.client, businessId, {
        tagNames: strList(tags),
        query
      }).then(this.client.pagination.loadRest), listLocationsForPropertyAndBusiness(this.client, propertyId, businessId, {
        enabled: true
      }).then(this.client.pagination.loadRest)]).then(([allLocations, addedLocations]) => subtractPagedLists(allLocations, addedLocations, (a, b) => a.id === b.id));
    }

    const params = {
      query,
      tagNames: strList(tags),
      enabled: true
    };
    return Promise.all([listLocationsForBusiness(this.client, businessId, {
      query,
      tagNames: strList(tags)
    }).then(this.client.pagination.loadRest).then(locations => {
      locations.items.forEach(l => {
        l.isEnabled = false; // eslint-disable-line no-param-reassign
      });
      return locations;
    }), listLocationsForPropertyAndBusiness(this.client, propertyId, businessId, params).then(this.client.pagination.loadRest)]).then(([allLocations, addedLocations]) => leftOuterJoinPagedLists(allLocations, addedLocations, (a, b) => a.id === b.id)).then(locations => {
      locations.items.forEach(location => {
        const entries = this.client.enrichment.read({
          propertyId,
          businessId,
          locationId: location.id
        });
        this.client.enrichment.applyAll(location, entries);
      });
      return locations;
    });
  }

  multiDetails(locationIds
  /*: [string]*/
  )
  /*: Promise<PagedList<Location>>*/
  {
    const _this5 = this;

    return _call(function () {
      // $FlowFixMe
      return _await(Promise.all(locationIds.map(id => _this5.for(id).details().catch(() => null))), function (locations
      /*: PagedList<Location>*/
      ) {
        // ignore missing Locations
        return new PagedList(locations.filter(Boolean));
      });
    });
  }

  search({
    /*: {
        query: string,
        tags: Array<string>,
        locationId: string,
        enabled?: boolean,
        limit: ?number,
        hasLiveOffers: boolean,
        hasRoles?: boolean,
        accessContext?: boolean
      }*/

    query,
    tags,
    locationId,
    enabled,
    limit,
    hasLiveOffers,
    hasRoles,
    accessContext
  } = {}, options
  /*: RequestOptions*/
  = {})
  /*: Promise<PagedList<Location>>*/
  {
    const _this6 = this;

    return _call(function () {
      const {
        propertyId,
        businessId
      } = _this6.context && _this6.context.serialize();

      if (tags && !propertyId) throw new Error('Cannot search by tags without a Property context');
      if (enabled !== undefined && !propertyId) throw new Error('Cannot search by enabled without a Property context');
      const path = [hasRoles ? '/self' : '', propertyId ? `/properties/${propertyId}` : '', businessId ? `/businesses/${businessId}` : '', `/locations/search`].join('');
      /* eslint-disable no-nested-ternary */

      const requestFields = options.fields || (propertyId && !businessId ? hasRoles ? selfPropertyLocationsSearchFields : propertyLocationsSearchFields : hasRoles ? selfSearchFields : searchFields);
      /* eslint-enable no-nested-ternary */

      const params = {
        q: strinct(query),
        locationId,
        tagNames: strList(tags),
        enabled,
        limit,
        hasLiveOffers,
        accessContext
      };
      const req = new _this6.client.Request(path, params, requestFields);
      req.parse = parse;
      return _await(_this6.client.get(req));
    });
  }

  searchMine({
    /*: { enabled?: boolean }*/

    enabled
  } = {}) {
    const _this7 = this;

    return _call(function () {
      return _await(_this7.client.access.searchMine(_this7.context).then(_this7.client.pagination.loadRest), function (locationResult) {
        // FIXME: Need a way to de-duplicate Locations through the API so we can avoid exhaustive paging
        const enabledFilter = enabled == null ? identity : item => item.isEnabled === enabled;
        return locationResult.copy(items => {
          const uniques = _.uniqBy(items, item => item.locationId);

          return uniques.map(item => item.location).filter(Boolean).map(parse).filter(enabledFilter);
        });
      });
    });
  } // TODO: Remove list. Rename listRaw to list.


  list(searchParams
  /*: any*/
  , options
  /*: RequestOptions*/
  )
  /*: Promise<PagedList<Location>>*/
  {
    return this.search(searchParams, options);
  }

  listRaw() {
    const {
      propertyId,
      businessId
    } = this.context && this.context.serialize() || {};
    const path = propertyId ? `/properties/${propertyId}/businesses/${businessId}/locations` : `/businesses/${businessId}/locations`;
    const rawFields = ['id', 'name', 'url', 'phone'];
    const req = new this.client.Request(path, {}, rawFields);
    return this.client.get(req);
  }

  create(location
  /*: Location*/
  )
  /*: Promise<Location>*/
  {
    const _this8 = this;

    return _call(function () {
      return _await(getUpdatedAddress(location.address, _this8.client), function (persistedAddress) {
        const {
          businessId
        } = _this8.context && _this8.context.serialize();

        const params = serializeCreate(location);
        params.addressId = persistedAddress.id;
        const req = new _this8.client.Request(`/businesses/${businessId}/locations`, params, fields);
        req.parse = parse;
        return _await(_this8.client.post(req), function (createdLocation
        /*: Location*/
        ) {
          const newId = createdLocation.id;
          if (!newId) throw new Error('Id missing from created Location');
          return _await(_this8.for(newId).updateSlug(location.slug || location.name), function (newSlug) {
            createdLocation.slug = newSlug;
            return createdLocation;
          });
        });
      });
    });
  }

}

module.exports = LocationsRepo;